import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import CommandPalette from './components/CommandPalette';
import AgentPanel from './components/agente/AgentPanel';
import SugestoesPanel from './components/agente/SugestoesPanel';
import './styles/tokens.css';
import './styles/components.css';

import { LogoutConfirmationModal } from '../components/Modais/Modal';
import SettingsDialog from './components/SettingsDialog';
import Toast from '../components/Modais/Toast';

import Painel from '../components/Painel/Painel';
import Clientes from '../components/Clientes/Clientes';
import Projetos from '../components/Projetos/Projetos';
import Financeiro from '../components/Financeiro/Financeiro';
import Estoque from '../components/Estoque/Estoque';
import Patrimonio from '../components/Patrimonio/Patrimonio';
import Documentos from '../components/Documentos/Documentos';

// Contexto de tela pro Agente (AGENTE_OPERACIONAL_IA_CRM.md §12) — traduz o
// `contexto` de navegação do CrmShell (que hoje só carrega o "porquê" da
// navegação INICIAL de um módulo, não o que o usuário está olhando agora
// dentro dele) num formato estável pro backend (`ToolContext.tela_contexto`,
// ex.: `create_project_task` usa `project_id` daqui como fallback). Limitação
// honesta: se o usuário troca de projeto/cliente DENTRO do módulo sem passar
// pelo CrmShell de novo (navegação interna do próprio módulo), esse contexto
// não acompanha — o Agente não erra por causa disso (as tools sempre pedem
// nome/id explícito quando o contexto não resolve), só não pré-preenche.
function contextoParaAgente(selecionado, contexto) {
  if (selecionado === 'projetos' && contexto.projetoId) {
    return { tela: 'projetos', entity_type: 'projeto', entity_id: contexto.projetoId, project_id: contexto.projetoId };
  }
  if (selecionado === 'clientes' && contexto.clienteId) {
    return { tela: 'clientes', entity_type: 'cliente', entity_id: contexto.clienteId, client_id: contexto.clienteId };
  }
  if (selecionado === 'financeiro' && contexto.clienteIdFinanceiro) {
    return { tela: 'financeiro', entity_type: 'cliente', entity_id: contexto.clienteIdFinanceiro, client_id: contexto.clienteIdFinanceiro };
  }
  if (selecionado === 'patrimonio' && contexto.ativoId) {
    return { tela: 'patrimonio', entity_type: 'ativo', entity_id: contexto.ativoId };
  }
  if (selecionado === 'documentos' && contexto.documentoId) {
    return { tela: 'documentos', entity_type: 'documento', entity_id: contexto.documentoId };
  }
  return { tela: selecionado };
}

function getCookie(nome) {
  const nomeEQ = nome + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nomeEQ) === 0) return c.substring(nomeEQ.length, c.length);
  }
  return null;
}

// Shell do CRM (§4.2, §50 Etapa A) — experiência separada do Cofre, com
// sidebar texto+ícone, topbar, app switcher e quick create. Módulos
// internos (Painel/Clientes/Projetos/...) permanecem os mesmos desta
// etapa — só o container/navegação mudou. Redesign de cada tela é
// Etapa B em diante.
function CrmShell() {
  const navigate = useNavigate();
  const [autorizado, setAutorizado] = useState(null);
  const [selecionado, setSelecionado] = useState('inicio');
  // Ponte contextual entre entidades (§8.1) — carrega o "porquê" de uma
  // navegação (ex.: abrir um Projeto específico, pré-preencher client_id
  // numa criação). Sempre resetado numa navegação "simples" (sidebar/topbar/
  // quick create/painel) pra nunca vazar contexto de uma tela pra outra sem
  // relação — só `navegar()` com um 2º argumento explícito carrega contexto.
  const [contexto, setContexto] = useState({});
  const navegar = (destino, novoContexto = {}) => {
    setContexto(novoContexto);
    setSelecionado(destino);
    setMobileNavOpen(false);
  };
  const [permissao, setPermissao] = useState(false);
  const [empresaNome, setEmpresaNome] = useState('');
  const [logoEmpresa, setLogoEmpresa] = useState(null);
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const [showPalette, setShowPalette] = useState(false);
  const [showAgentPanel, setShowAgentPanel] = useState(false);
  const [showSugestoes, setShowSugestoes] = useState(false);
  // Contagem de sugestões pendentes pro badge da topbar. Endpoint separado
  // (COUNT, não a lista) pra manter o poll barato — a lista completa só é
  // carregada quando o painel abre.
  const [sugestoesPendentes, setSugestoesPendentes] = useState(0);
  // Tema claro é o padrão (Fase 2 do redesign, 2026-08) — escuro continua
  // disponível como opção, persistida por navegador (não por tenant/back-
  // end, é preferência pessoal de exibição, não dado de negócio).
  const [tema, setTema] = useState(() => localStorage.getItem('crm_tema') || 'light');
  useEffect(() => { localStorage.setItem('crm_tema', tema); }, [tema]);

  // Colapso da sidebar (§9 — "desktop menor: permitir collapse", escolha
  // manual do usuário, persistida por navegador) e estado do drawer mobile
  // (nunca persistido — sempre fechado ao carregar/trocar de tela).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('crm_sidebar_collapsed') === '1');
  useEffect(() => { localStorage.setItem('crm_sidebar_collapsed', sidebarCollapsed ? '1' : '0'); }, [sidebarCollapsed]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const MODULOS_PADRAO_FECHADO = { financeiro: false, projetos: false, estoque: false, patrimonio: false };
  const [modulosHabilitados, setModulosHabilitados] = useState(MODULOS_PADRAO_FECHADO);
  const [crmAcessivel, setCrmAcessivel] = useState(false);

  const showToast = (message, type, className = '') => {
    setToast({ message, type, visible: true, className });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };

  const fetchDetalheUsuario = async () => {
    try {
      const token_autorizado = getCookie('info');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/usuarios/buscar`, {
        method: 'GET',
        headers: { 'Authority': token_autorizado },
      });
      const result = await response.json();
      if (result.status === 'ok') {
        setPermissao(result.cargo === 'Admin' || result.is_super_admin);
        setEmpresaNome(result.empresa_nome || '');
        setNomeUsuario((result.usuario?.nome || result.nome || '').split(' ')[0] || '');
      } else {
        showToast(result.mensagem || 'Erro ao carregar dados do usuário', 'error');
      }
    } catch (error) {
      showToast('Erro ao carregar dados do usuário', 'error');
    }
  };

  const fetchModulosHabilitados = async () => {
    try {
      const token_autorizado = getCookie('info');
      const base = process.env.REACT_APP_GESTAO_API_URL || '';
      const response = await fetch(`${base}/api/modulos/meus`, {
        method: 'GET',
        headers: { 'Authority': token_autorizado },
      });
      const result = await response.json();
      if (result.status === 'ok') {
        setModulosHabilitados(result.modulos);
        setCrmAcessivel(true);
      } else {
        setModulosHabilitados(MODULOS_PADRAO_FECHADO);
        setCrmAcessivel(false);
      }
    } catch (error) {
      setModulosHabilitados(MODULOS_PADRAO_FECHADO);
      setCrmAcessivel(false);
    }
  };

  // Badge de sugestões pendentes. Endpoint de CONTAGEM (não a lista): o
  // custo precisa ser desprezível porque isto roda em intervalo.
  const atualizarSugestoes = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_GESTAO_API_URL || '';
      const resp = await fetch(`${base}/api/automacoes/sugestoes/pendentes/contagem`, {
        headers: { 'Authority': getCookie('info') },
      });
      const result = await resp.json();
      if (result.status === 'ok') setSugestoesPendentes(result.pendentes || 0);
    } catch (e) {
      // Badge é informação secundária — falhar aqui não pode quebrar a tela.
    }
  }, []);

  useEffect(() => {
    const token_autorizado = getCookie('info');
    if (!token_autorizado) {
      navigate('/');
      return;
    }
    setAutorizado(true);
    fetchDetalheUsuario();
    fetchModulosHabilitados();
    atualizarSugestoes();
    // Logo da empresa: carregada uma vez na abertura do CRM. Se a pessoa
    // trocar em Configurações, o próprio diálogo avisa por `onLogoAlterada`
    // — não há poll para algo que muda raramente.
    fetch(`${process.env.REACT_APP_GESTAO_API_URL || ''}/api/modulos/logo`, { headers: { 'Authority': getCookie('info') } })
      .then((r) => r.json()).then((r) => { if (r.status === 'ok') setLogoEmpresa(r.logo || null); })
      .catch(() => {});
    // O job de fundo processa a outbox a cada 5 min; 60s aqui deixa o badge
    // aparecer logo depois sem transformar a topbar num poller agressivo.
    const timer = setInterval(atualizarSugestoes, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cmd/Ctrl+K abre a paleta de comandos de qualquer tela do CRM (item 3/4
  // do diagnóstico de usabilidade) — global, não fica preso a um módulo.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowPalette(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const confirmLogout = () => {
    document.cookie = 'info=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    window.location.href = '/';
  };

  if (autorizado === null) return null;

  return (
    <div className="crm-app" data-theme={tema}>
      <div className="crm-shell">
        <Sidebar
          selecionado={selecionado}
          onSelecionar={(id) => navegar(id)}
          modulosHabilitados={modulosHabilitados}
          permissaoClientes={permissao}
          crmAcessivel={crmAcessivel}
          empresaNome={empresaNome}
          logoEmpresa={logoEmpresa}
          onAbrirConfig={() => setShowConfigModal(true)}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
          mobileOpen={mobileNavOpen}
          onCloseMobile={() => setMobileNavOpen(false)}
          nomeUsuario={nomeUsuario}
        />
        <div className="crm-main">
          <Topbar
            selecionado={selecionado}
            onSelecionar={(id) => navegar(id)}
            modulosHabilitados={modulosHabilitados}
            permissaoClientes={permissao}
            crmAcessivel={crmAcessivel}
            onLogout={() => setShowLogoutConfirmation(true)}
            onAbrirBusca={() => setShowPalette(true)}
            tema={tema}
            onAlternarTema={() => setTema((t) => (t === 'dark' ? 'light' : 'dark'))}
            onAbrirMenuMobile={() => setMobileNavOpen(true)}
            nomeUsuario={nomeUsuario}
            empresaNome={empresaNome}
          logoEmpresa={logoEmpresa}
            onAbrirConfig={() => setShowConfigModal(true)}
            onAbrirAgente={() => setShowAgentPanel(true)}
            onAbrirSugestoes={() => setShowSugestoes(true)}
            sugestoesPendentes={sugestoesPendentes}
          />
          <div className="crm-content">
            {selecionado === 'inicio' && crmAcessivel ? (
              <Painel modulosHabilitados={modulosHabilitados} permissaoClientes={permissao} onNavegar={(id) => navegar(id)} nomeUsuario={nomeUsuario} />
            ) : selecionado === 'clientes' && crmAcessivel && permissao ? (
              <Clientes showToast={showToast} onNavegar={navegar} clienteIdInicial={contexto.clienteId} />
            ) : selecionado === 'projetos' && modulosHabilitados.projetos ? (
              <Projetos
                showToast={showToast}
                modulosHabilitados={modulosHabilitados}
                projetoIdInicial={contexto.projetoId}
                clienteIdParaNovoProjeto={contexto.clienteIdParaNovoProjeto}
                onNavegar={navegar}
              />
            ) : selecionado === 'financeiro' && modulosHabilitados.financeiro ? (
              <Financeiro
                showToast={showToast}
                clienteIdContexto={contexto.clienteIdFinanceiro}
                abrirCriacaoTipo={contexto.abrirCriacaoFinanceiro}
                onNavegar={navegar}
              />
            ) : selecionado === 'estoque' && modulosHabilitados.estoque ? (
              <Estoque showToast={showToast} />
            ) : selecionado === 'patrimonio' && modulosHabilitados.patrimonio ? (
              <Patrimonio showToast={showToast} ativoIdInicial={contexto.ativoId} onNavegar={navegar} />
            ) : selecionado === 'documentos' && crmAcessivel ? (
              <Documentos showToast={showToast} documentoIdInicial={contexto.documentoId} relacaoInicial={contexto.relacaoDocumento} onNavegar={navegar} />
            ) : (
              <Painel modulosHabilitados={modulosHabilitados} permissaoClientes={permissao} onNavegar={(id) => navegar(id)} nomeUsuario={nomeUsuario} />
            )}
          </div>
        </div>
      </div>

      {toast.visible && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '', visible: false })} />
      )}

      <CommandPalette
        open={showPalette}
        onClose={() => setShowPalette(false)}
        onNavegar={navegar}
        modulosHabilitados={modulosHabilitados}
        permissaoClientes={permissao}
        crmAcessivel={crmAcessivel}
      />

      <LogoutConfirmationModal
        show={showLogoutConfirmation}
        fecharModal={() => setShowLogoutConfirmation(false)}
        handleConfirm={confirmLogout}
      />

      <SettingsDialog open={showConfigModal} onClose={() => setShowConfigModal(false)} showToast={showToast} onLogoAlterada={setLogoEmpresa} />

      <SugestoesPanel
        open={showSugestoes}
        onClose={() => setShowSugestoes(false)}
        onAlterou={atualizarSugestoes}
        showToast={showToast}
      />

      <AgentPanel
        open={showAgentPanel}
        onClose={() => setShowAgentPanel(false)}
        onNavegar={navegar}
        telaContexto={contextoParaAgente(selecionado, contexto)}
      />
    </div>
  );
}

export default CrmShell;
